import { createSlice, createAction, createSelector } from '@reduxjs/toolkit';
import { TimeWarningAlarm } from 'features/notificationDrawer/types';
import { parseTraceFromBuffer } from 'utils/traceUtils';
import { RootState } from '../../app/store';
import { Analyzer, UpdateSnapshotDto } from '../../types';
import { InstrumentForm } from '../instrumentConfiguration/types';
import { SocketAlarm, TraceData } from './types';

interface InitialState {
  host: string;
  message: { message: string; type: string }[];
  traceData: {
    [key: number]: TraceData;
  };
  alarms: SocketAlarm[];
  connected: boolean;
  timeWarningAlarms: TimeWarningAlarm[];
  connectedUsers: number[];
  inTimeshare: boolean;
  timeshareUsers: { id: number; snapshot_id: number }[];
  emailTestSuccess: boolean | undefined;
}

export const spectrasWebSocketServerSlice = createSlice({
  name: 'spectrasWebSocketServer',
  initialState: {
    host: '',
    message: [],
    traceData: {},
    alarms: [],
    timeWarningAlarms: [],
    connected: false,
    connectedUsers: [],
    inTimeshare: false,
    timeshareUsers: [],
    emailTestSuccess: undefined,
  } as InitialState,
  reducers: {
    wsConnected: (state) => {
      state.connected = true;
    },
    wsDisconnected: (state) => {
      state.connected = false;
    },
    updateMessage: (state, action) => {
      if (!action.payload?.snapshot_id) {
        state.message[action.payload.analyzer_id] = action.payload;
      } else {
        state.message[action.payload.snapshot_id] = action.payload;
      }
    },
    updateConnectedUsers: (state, action) => {
      if (action.payload.userIds) state.connectedUsers = action.payload.userIds;
    },
    updateAlarm: (state, action) => {
      state.alarms.push(action.payload);
    },
    updateTimeWarningAlarm: (state, action) => {
      state.timeWarningAlarms.push(action.payload);
    },
    updateTraceData: (state, action) => {
      for (let i = 0; i < action.payload.length; i++) {
        const newData = action.payload[i];
        const dataArray: number[] = Array.from(new Int16Array(newData));

        const analyzerId = dataArray[0];
        const snapshotId = dataArray[1];

        dataArray.splice(0, 2);
        const parsedTrace = dataArray.map((tracePoint) => tracePoint / 100.0);

        if (!state.traceData[snapshotId]?.trace) {
          state.traceData[snapshotId] = {
            analyzer_id: analyzerId,
            snapshot_id: snapshotId,
            trace: parsedTrace,
          };

          continue;
        }

        state.traceData[snapshotId].trace = parsedTrace;
      }
    },
    updateTimeshareStatus: (state, { payload }) => {
      state.inTimeshare = payload?.action === 'joined';
      state.timeshareUsers = payload?.users;
    },
    updateTimeshareUsers: (state, { payload }) => {
      state.timeshareUsers = payload?.users;
    },
    updateEmailTestStatus: (state, { payload }) => {
      const { status } = payload;
      state.emailTestSuccess = status.includes('Failed') ? false : true;
    },
    clearEmailTestStatus: (state) => {
      state.emailTestSuccess = undefined;
    },
    clearTraceDataAndMessages: (state) => {
      state.traceData = {};
      state.message = [];
    },
    clearMessages: (state) => {
      state.message = [];
    },
    removeAlarm: (state, { payload }) => {
      state.alarms.splice(payload, 1);
    },
    removeTimeWarningAlarm: (state, { payload }) => {
      state.timeWarningAlarms.splice(payload, 1);
    },
    removeAllAlarms: (state) => {
      state.alarms = [];
      state.timeWarningAlarms = [];
    },
    removeAlarmByLogId: (state, { payload }) => {
      state.alarms = state.alarms.filter(
        (alarm) => alarm.alarm_log_id !== payload
      );
    },
  },
});

export const wsConnect = createAction('wsConnect');
export const wsDisconnect = createAction('wsDisconnect');
export const newMessage = createAction('newMessage');
export const requestUsers = createAction<{ user_id: number }>('requestUsers');
export const testEmail = createAction<{
  email_settings: {
    EmailSmtp: string | undefined;
    EmailPort: number | undefined;
    EmailUserName: string | undefined;
    EmailPassword: string | undefined;
    EmailFrom: string | undefined;
    EmailRecipient: string | undefined;
    EmailSubject: string | undefined;
    EmailMessage: string | undefined;
  };
  user_id: number;
}>('testEmail');
export const testSnmp = createAction<any>('testSnmp');
export const subscribeToAnalyzers = createAction<{
  user_id: number;
  analyzer_ids: number[];
}>('subscribeToAnalyzers');
export const joinTimeshare = createAction<{
  user_id: number;
  snapshot_id: number;
  analyzer_id: number;
}>('joinTimeshare');
export const leaveTimeshare = createAction<{ user_id: number }>(
  'leaveTimeshare'
);
export const joinWatchTimeshare = createAction<{
  user_id: number;
  analyzer_id: number;
}>('joinWatchTimeshare');
export const leaveWatchTimeshare = createAction<{
  user_id: number;
}>('leaveWatchTimeshare');
export const bootTimeshareUser = createAction<{
  user_id: number;
  user_id_to_boot: number;
}>('bootTimeshareUser');
export const unsubscribeFromAllAnalyzers = createAction(
  'unsubscribeFromAllAnalyzers'
);
export const subscribeToCommands = createAction<{ user_id: number }>(
  'subscribeToCommands'
);
export const testConnection = createAction<{ instrument: InstrumentForm }>(
  'testConnection'
);
export const updateAnalyzerProperties = createAction<{
  properties: UpdateSnapshotDto;
  analyzer: Analyzer;
  user_id: number;
}>('updateAnalyzerProperties');
export const updateMinMax = createAction<{
  properties: UpdateSnapshotDto;
  analyzer: Analyzer;
  user_id: number;
}>('updateMinMax');
export const resetMinMax = createAction('resetMinMax');
export const sendScpiCommand = createAction<{
  analyzer: Analyzer;
  scpiCommand: string;
}>('sendScpiCommand');

export const {
  wsConnected,
  wsDisconnected,
  updateMessage,
  updateAlarm,
  updateTimeWarningAlarm,
  updateTraceData,
  updateTimeshareStatus,
  updateTimeshareUsers,
  updateConnectedUsers,
  updateEmailTestStatus,
  clearEmailTestStatus,
  clearTraceDataAndMessages,
  clearMessages,
  removeAlarm,
  removeAllAlarms,
  removeTimeWarningAlarm,
  removeAlarmByLogId,
} = spectrasWebSocketServerSlice.actions;

export const selectConnectionStatus = (state: RootState) =>
  state.spectrasWebSocketServer.connected;
export const selectMessage = (state: RootState) =>
  state.spectrasWebSocketServer.message;
export const selectAlarms = (state: RootState) =>
  state.spectrasWebSocketServer.alarms;
export const selectTimeWarningAlarms = (state: RootState) =>
  state.spectrasWebSocketServer.timeWarningAlarms;
export const selectTraceData = (state: RootState) =>
  state.spectrasWebSocketServer.traceData;
export const selectTraceDataByAnalyzerId = (
  state: RootState,
  analyzerId: number
) => state.spectrasWebSocketServer.traceData[analyzerId]?.trace;
export const selectTraceDataBySnapshotId = (
  state: RootState,
  snapshotId: number | undefined | null
) => {
  return state.spectrasWebSocketServer.traceData[snapshotId || -1]?.trace;
};
export const selectCurrentTraceLength = (
  state: RootState,
  snapshotId: number
) => state.spectrasWebSocketServer.traceData[snapshotId]?.trace.length;
export const selectInTimeshare = (state: RootState) =>
  state.spectrasWebSocketServer.inTimeshare;
export const selectTimeshareUsers = (state: RootState) =>
  state.spectrasWebSocketServer.timeshareUsers;

export default spectrasWebSocketServerSlice.reducer;

export const selectTraceLength = () =>
  createSelector(
    [
      (state: RootState) => state.snapshots.entities,
      (state: RootState, snapshotId: number) => snapshotId,
      selectCurrentTraceLength,
    ],
    (snapshots, snapshotId, currentTraceLength) => {
      if (typeof currentTraceLength === 'number' && currentTraceLength !== 0)
        return currentTraceLength;
      const lastTrace = snapshots[snapshotId]?.last_trace;
      if (lastTrace === undefined) return false;
      const parsedLastTrace = parseTraceFromBuffer(lastTrace);
      return parsedLastTrace.length;
    }
  );

export const selectSnapshotAlarming = () =>
  createSelector(
    [
      (state: RootState) => state.spectrasWebSocketServer.alarms,
      (state: RootState, snapshotId: number) => snapshotId,
    ],
    (alarms, snapshotId) => {
      if (snapshotId === -1)
        return {
          alarming: false,
          mostRecentIgnoreInterval: '',
          mostRecentTimestamp: '',
          startStopMode: false,
          warning: false,
        };
      for (let i = alarms.length - 1; i >= 0; i--) {
        // Find the most recent alarm involving the snapshot
        if (
          alarms[i].alarm_log_items.some(
            (logItem) => logItem.snapshot_id === snapshotId
          )
        ) {
          if (!(alarms[i].stop_mode || alarms[i].start_mode)) {
            return {
              alarming: true,
              mostRecentTimestamp: alarms[i].timestamp,
              mostRecentIgnoreInterval: alarms[i].ignore_interval,
              startStopMode: false,
              warning: alarms[i].warning,
            };
            //  If alarm is in start OR stop mode, check more recent alarms for a stop message.
            //  We have to manually check for a stop message because an alarm can stop when you
            //  remove alarming snapshots, potentially stranding a snapshot as alarming forever
            //  for start, although a message never appears, stop is still logged just doesn't notify the user.
            //  In this case, we group start and stop as one variable as they do the same thing ultimately
          } else {
            const alarmId = alarms[i].alarm_id;
            for (let j = i; j < alarms.length; j++) {
              if (
                alarms[j].alarm_id === alarmId &&
                alarms[j].stopped === true
              ) {
                return {
                  alarming: false,
                  mostRecentTimestamp: '',
                  mostRecentIgnoreInterval: '',
                  startStopMode: true,
                  warning: false,
                };
              }
            }
            return {
              alarming: true,
              mostRecentTimestamp: alarms[i].timestamp,
              mostRecentIgnoreInterval: alarms[i].ignore_interval,
              startStopMode: true,
              warning: alarms[i].warning,
            };
          }
        }
      }
      return {
        alarming: false,
        mostRecentIgnoreInterval: '',
        mostRecentTimestamp: '',
        startStopMode: false,
      };
    }
  );
